import * as React from "react";
import { useState } from "react";
import {
  Menu,
  MenuButton,
  MenuList,
  MenuPopover,
  MenuTrigger,
  slot,
  useMergedRefs,
  useOverflowMenu,
} from "@fluentui/react-components";
import { iconNameToIconProps } from "@1js/acui-icons";
import { Icon } from "@fluentui/react";
import type {
  AppTabListOverflowMenuProps,
  AppTabListOverflowMenuState,
} from "./AppTabListOverflowMenu.types";
import type { OnExecuteParameter } from "@1js/acui-common";
import { useAppComponentKeytip, deriveTabKeytip } from "@1js/acui-keytip";
import { useAppComponentTooltips } from "@1js/acui-tooltip";
import { pivotOverflowButtonId } from "@1js/acui-pivot";

export const useAppTabListOverflowMenu = (
  props: AppTabListOverflowMenuProps,
  ref: React.Ref<HTMLButtonElement>
): AppTabListOverflowMenuState => {
  const {
    ariaLabel,
    topItems,
    overflowItems,
    overflowTabHeaderMenuLauncherIcon,
    onMenuButtonClick,
    onMenuItemClick,
    onOverflowButtonExecute,
    keytipProps,
  } = props;
  const { ref: overflowRef, isOverflowing } =
    useOverflowMenu<HTMLButtonElement>();

  const [menuOpened, setMenuOpened] = useState(false);

  const root = slot.always(props.root, {
    defaultProps: {
      hasIcons: false,
    },
    elementType: Menu,
  });

  const menuTrigger = slot.always(props.menuTrigger, {
    defaultProps: { disableButtonEnhancement: true },
    elementType: MenuTrigger,
  });

  const handleMenuButtonClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setMenuOpened(!menuOpened);
    onMenuButtonClick(event);
  };

  const onExecute = React.useCallback(
    (param: OnExecuteParameter) => {
      if (onOverflowButtonExecute) {
        onOverflowButtonExecute(param);
      }
    },
    [onOverflowButtonExecute]
  );

  // Calculate final ref to use with tooltips and keytips
  const { mergedRef } = useAppComponentTooltips(ref, {
    label: ariaLabel,
    hideLabel: true,
  });

  const mergedRefWithKeytip = useAppComponentKeytip(
    {
      id: pivotOverflowButtonId,
      onExecute,
    },
    mergedRef,
    "AppButton",
    deriveTabKeytip,
    { keytipProps }
  );

  const triggerIcon = slot.always(
    props.triggerIcon ?? {
      children: (
        <Icon {...iconNameToIconProps(overflowTabHeaderMenuLauncherIcon)} />
      ),
    },
    { elementType: "span" }
  );

  const trigger = slot.always(props.trigger, {
    defaultProps: {
      id: pivotOverflowButtonId,
      type: "button",
      "aria-label": ariaLabel,
      appearance: "subtle",
      ref: useMergedRefs<HTMLElement | null | undefined>(
        overflowRef,
        mergedRefWithKeytip
      ),
      size: "small",
      onClick: handleMenuButtonClick,
      menuIcon: triggerIcon,
    },
    elementType: MenuButton,
  });

  const menuPopover = slot.always(props.menuPopover, {
    elementType: MenuPopover,
  });
  const menuList = slot.always(props.menuList, { elementType: MenuList });
  const menuListWrapper = slot.always(props.menuListWrapper, {
    elementType: "div",
  });

  return {
    isOverflowing,
    topItems,
    overflowItems,
    onMenuItemClick,

    components: {
      root: Menu,
      menuTrigger: MenuTrigger,
      trigger: MenuButton,
      triggerIcon: "span",
      menuPopover: MenuPopover,
      menuList: MenuList,
      menuListWrapper: "div",
    },

    root,
    menuTrigger,
    trigger,
    triggerIcon,
    menuPopover,
    menuList,
    menuListWrapper,
    menuOpened,
  };
};
